import { documentToPlainTextString } from '@contentful/rich-text-plain-text-renderer';
import { GetStaticPaths, GetStaticPropsContext, InferGetStaticPropsType } from 'next';
import { useTranslation } from 'next-i18next';

import { CHANNEL, inspirationPageSlugs } from '@boss/constants/b2b-b2c';
import { getPaths } from '@boss/contentful-client';
import {
  getEnrichedInspirationTopic,
  getInspirationLocalizedSlugs,
  inspiration,
} from '@boss/contentful-client/b2b-b2c';
import { color as colorService, mapNextToContentfulMarket } from '@boss/services';
import { Color, Environment } from '@boss/types/b2b-b2c';
import { defaultLocale } from 'config/locales.config';

import { SEO } from '../../components';
import { PageTypes } from '../../constants';
import InspirationDetailPage from '../../containers/inspirationDetailPage';
import Layout from '../../layout/default';
import getServerSideTranslations from '../../utils/getServerSideTranslations';
import { mapContentfulToNextLocale, mapNextToContentfulLocaleMarket } from '../../utils/localeMapper';
import { getMappedNavigation } from '../../utils/navigation';

export const config = {
  maxDuration: 30,
};

export const getStaticPaths: GetStaticPaths = async ({ locales }) => {
  return await getPaths({
    preview: false,
    channel: CHANNEL,
    contentType: 'inspirationImage',
    localeMapper: mapContentfulToNextLocale,
    locales: locales as string[],
  });
};

export const getStaticProps = async ({ preview, locale, params }: GetStaticPropsContext) => {
  const slug = params?.slug;

  if (!slug || Array.isArray(slug) || !locale || locale === 'default') {
    return {
      notFound: true,
    };
  }
  const contentfulLocaleMarket = mapNextToContentfulLocaleMarket(locale);
  const market = mapNextToContentfulMarket(locale);

  const [navigation, inspirationTopic] = await Promise.all([
    getMappedNavigation(contentfulLocaleMarket, !!preview),
    getEnrichedInspirationTopic(slug, contentfulLocaleMarket, defaultLocale, CHANNEL, !!preview, market),
  ]);

  if (!inspirationTopic) {
    return {
      notFound: true,
    };
  }

  const colorIds = inspirationTopic.usedColors?.map(color => color.colorId);

  const colors = colorIds?.length
    ? await colorService.getColorByColorIds(colorIds, contentfulLocaleMarket, CHANNEL)
    : [];
  const usedColors = colors
    .filter(item => !!item && !!item.colorid)
    ?.map(item => (item ? colorService.mapColor(item) : null)) as Color[];

  // Get the other translated slugs for navigation
  const localizedSlugs = { ...inspirationPageSlugs };
  const otherSlugs = await getInspirationLocalizedSlugs({
    preview: !!preview,
    locales: Object.keys(inspirationPageSlugs)
      .filter(otherLocale => otherLocale !== locale)
      .map(mapNextToContentfulLocaleMarket),
    channel: CHANNEL,
    id: inspirationTopic.id,
    market: mapNextToContentfulMarket(locale),
  });

  localizedSlugs[locale] += `/${inspirationTopic.slug}`;

  for (const local of Object.keys(inspirationPageSlugs)) {
    const slug = otherSlugs.find(slug => {
      return slug.locale === mapNextToContentfulLocaleMarket(local);
    });

    if (!slug) {
      continue;
    }

    localizedSlugs[local] += `/${slug.slug}`;
  }

  const relatedImages = (
    await inspiration.getRelatedInspirationImage({
      refCreationDate: inspirationTopic.createdAt,
      locale: contentfulLocaleMarket,
      entryId: inspirationTopic.id,
      channel: CHANNEL,
      market: mapNextToContentfulMarket(locale),
    })
  ).map(img => ({ ...img, slug: `${inspirationPageSlugs[locale]}/${img.slug}` }));

  return {
    props: {
      pageType: PageTypes.INSPIRATION_DETAIL,
      relatedImages,
      locale,
      preview: !!preview,
      navigation,
      pageData: { inspirationTopic },
      usedColors,
      localizedSlugs,
      slug,
      environment: process.env.CONTENTFUL_ENVIRONMENT as Environment,
      ...(await getServerSideTranslations(locale, ['common', 'product', 'inspiration', 'forms'])),
    },
  };
};

export const Index = ({
  pageData,
  navigation,
  preview,
  environment,
  locale,
  relatedImages,
  localizedSlugs,
  slug,
  usedColors,
}: InferGetStaticPropsType<typeof getStaticProps>) => {
  const { t } = useTranslation();
  const { inspirationTopic } = pageData;
  const pageTitle = inspirationTopic.title
    ? documentToPlainTextString(inspirationTopic.title)
    : slug.replace(/-/g, ' ');
  const pageDescription = inspirationTopic.description
    ? documentToPlainTextString(inspirationTopic.description)
    : pageTitle;
  const crumbles = [
    { href: inspirationPageSlugs[locale], label: t('inspirationImagesLabel', { ns: 'inspiration' }) },
    { href: slug, label: pageTitle },
  ];

  return (
    <SEO
      breadcrumbs={crumbles}
      description={pageDescription}
      imageSrc={inspirationTopic.image?.image?.file?.url}
      localizedSlugs={localizedSlugs}
      navigation={navigation}
      title={pageTitle}
    >
      <Layout
        crumbles={crumbles}
        environment={environment}
        localizedSlugs={localizedSlugs}
        navigation={navigation}
        preview={preview}
      >
        <InspirationDetailPage
          inspiration={inspirationTopic}
          locale={locale}
          relatedImages={relatedImages}
          usedColors={usedColors}
        />
      </Layout>
    </SEO>
  );
};

export default Index;
